import { Checkbox, FormControlLabel, Stack, Tooltip } from '@mui/material';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { FormTextField } from 'components/index';
import {
  WebviewButtonLinkStepFormFieldsLinkNames,
  WebviewButtonLinkStepFormFieldsNames,
} from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/webview-button-link/types';
import { WebviewButtonLinkFormFieldsConfig } from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/webview-button-link/utils';

const WebviewButtonLinkFormField = () => {
  const { setValue, control } = useFormContext();
  const { id: projectId } = useParams();

  const linkData = useWatch({
    control,
    name: WebviewButtonLinkStepFormFieldsNames.Link,
  });

  const {
    name: urlName,
    label: urlLabel,
    placeholder: urlPlaceholder,
    ...urlRest
  } = WebviewButtonLinkFormFieldsConfig[WebviewButtonLinkStepFormFieldsLinkNames.Url];

  const {
    name: textName,
    label: textLabel,
    placeholder: textPlaceholder,
    ...textRest
  } = WebviewButtonLinkFormFieldsConfig[WebviewButtonLinkStepFormFieldsLinkNames.Text];

  const { label: includeProjectParamsLabel } =
    WebviewButtonLinkFormFieldsConfig[
      WebviewButtonLinkStepFormFieldsLinkNames.IncludeProjectParams
    ];

  const { label: waitingForCompleteLabel } =
    WebviewButtonLinkFormFieldsConfig[WebviewButtonLinkStepFormFieldsLinkNames.WaitingForComplete];

  const handleIncludeProjectParamsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    const currentUrl = linkData?.url || '';

    if (isChecked && currentUrl && projectId) {
      // Add projectId to URL
      try {
        const urlObj = new URL(
          currentUrl.startsWith('http') ? currentUrl : `https://${currentUrl}`,
        );
        urlObj.searchParams.set('projectId', projectId);
        const updatedUrl = urlObj.toString();

        setValue(
          `${WebviewButtonLinkStepFormFieldsNames.Link}.${WebviewButtonLinkStepFormFieldsLinkNames.Url}`,
          updatedUrl,
        );
      } catch (error) {
        // If URL parsing fails, just update the checkbox state
      }
    } else if (!isChecked && currentUrl) {
      // Remove projectId from URL
      try {
        const urlObj = new URL(
          currentUrl.startsWith('http') ? currentUrl : `https://${currentUrl}`,
        );
        urlObj.searchParams.delete('projectId');
        const updatedUrl = urlObj.toString();

        setValue(
          `${WebviewButtonLinkStepFormFieldsNames.Link}.${WebviewButtonLinkStepFormFieldsLinkNames.Url}`,
          updatedUrl,
        );
      } catch (error) {
        // If URL parsing fails, just update the checkbox state
      }
    }

    setValue(
      `${WebviewButtonLinkStepFormFieldsNames.Link}.${WebviewButtonLinkStepFormFieldsLinkNames.IncludeProjectParams}`,
      isChecked,
    );
  };

  return (
    <Stack gap={2}>
      <FormTextField
        size='small'
        label={textLabel}
        placeholder={textPlaceholder}
        name={`${WebviewButtonLinkStepFormFieldsNames.Link}.${textName}`}
        {...textRest}
      />
      <FormTextField
        size='small'
        label={urlLabel}
        placeholder={urlPlaceholder}
        name={`${WebviewButtonLinkStepFormFieldsNames.Link}.${urlName}`}
        {...urlRest}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={linkData?.includeProjectParams || false}
            onChange={handleIncludeProjectParamsChange}
            size='small'
          />
        }
        label={includeProjectParamsLabel}
        sx={{ alignSelf: 'flex-start' }}
      />
      <Tooltip
        title='The webview has a process and should trigger the complete in order to proceed to next node'
        placement='top'
        arrow
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={linkData?.waitingForComplete || false}
              onChange={(event) => {
                setValue(
                  `${WebviewButtonLinkStepFormFieldsNames.Link}.${WebviewButtonLinkStepFormFieldsLinkNames.WaitingForComplete}`,
                  event.target.checked,
                );
              }}
              size='small'
            />
          }
          label={waitingForCompleteLabel}
          sx={{ alignSelf: 'flex-start' }}
        />
      </Tooltip>
    </Stack>
  );
};

export default WebviewButtonLinkFormField;
